import Hero from "../components/Hero";
import ContactForm from "../components/ContactForm";
import hotelImg from "../assests/images/hotel-bg.jpg";

import {
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";

import { Helmet } from "react-helmet-async";

export default function ContactUs() {
  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>Contact Us | Dubai Marina</title>

        <meta
          name="description"
          content="Contact Dubai Marina for hotel bookings, travel services, tours, visa assistance, car rentals and travel support."
        />

        <link
          rel="canonical"
          href="https://beachhoteldubai.com/contact"
        />

        <meta
          property="og:title"
          content="Contact Us | Dubai Marina"
        />

        <meta
          property="og:description"
          content="Get in touch with Dubai Marina for hotel bookings, travel services and assistance."
        />

        <meta
          property="og:url"
          content="https://beachhoteldubai.com/contact"
        />

        <meta property="og:type" content="website" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Contact Us | Dubai Marina"
        />

        <meta
          name="twitter:description"
          content="Contact Dubai Marina for hotel bookings, travel services and travel assistance."
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="w-full overflow-hidden bg-[#F5F8FA]">

        {/* HERO */}
        <Hero
          variant="inner"
          title="Contact Us"
          subtitle="We're here to help with your hotel booking and travel needs."
          backgroundImage={hotelImg}
        />

        {/* CONTACT INTRO */}
        <section className="relative overflow-hidden bg-[#F5F8FA] py-16 sm:py-20 lg:py-24">

          <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#8B0000]/5 blur-3xl" />

          <div className="pointer-events-none absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-[#D7A74A]/10 blur-3xl" />

          <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="mx-auto max-w-3xl text-center">

              {/* LABEL */}
              <div className="inline-flex items-center gap-2 rounded-full border border-[#8B0000]/10 bg-white px-4 py-2 shadow-sm">

                <span className="h-2 w-2 rounded-full bg-[#8B0000]" />

                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B0000]">
                  Get In Touch
                </span>

              </div>

              {/* HEADING */}
              <h1 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight text-[#172B35] sm:text-4xl lg:text-5xl">

                Let's Talk About

                <span className="mt-1 block text-[#8B0000]">
                  Your Next Journey
                </span>

              </h1>

              <div className="mx-auto mt-5 h-1 w-16 rounded-full bg-[#D7A74A]" />

              {/* DESCRIPTION */}
              <p className="mx-auto mt-6 max-w-2xl text-sm leading-8 text-[#71808A] sm:text-base">
                Have a question about a hotel, booking or travel
                service? Send us a message and our team will be
                happy to assist you.
              </p>

            </div>
          </div>
        </section>

        {/* CONTACT INFORMATION + FORM */}
        <section className="relative overflow-hidden bg-[#F5F8FA] pb-16 sm:pb-20 lg:pb-28">

          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            <div className="grid grid-cols-1 gap-7 lg:grid-cols-[400px_minmax(0,1fr)] xl:gap-10">

              {/* LEFT CONTACT INFORMATION */}
              <div className="group relative overflow-hidden rounded-[28px] bg-[#8B0000] p-7 shadow-[0_25px_70px_rgba(139,0,0,0.16)] sm:p-9 lg:p-10">

                {/* Decorative elements */}
                <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full border border-white/10" />

                <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/[0.04]" />

                <div className="pointer-events-none absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-[#D7A74A]/10 blur-2xl" />

                <div className="relative">

                  {/* LABEL */}
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] px-4 py-2">

                    <span className="h-1.5 w-1.5 rounded-full bg-[#D7A74A]" />

                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3D48A]">
                      Contact Information
                    </span>

                  </div>

                  {/* HEADING */}
                  <h2 className="mt-6 text-2xl font-extrabold leading-tight !text-white sm:text-3xl">

                    We're Always

                    <span className="block text-[#F3D48A]">
                      Happy To Help
                    </span>

                  </h2>

                  <div className="mt-4 h-1 w-12 rounded-full bg-[#D7A74A]" />

                  <p className="mt-5 text-sm leading-7 text-white/70">
                    Whether you need help with a hotel
                    booking, travel service or general
                    enquiry, our team is ready to assist.
                  </p>

                  {/* CONTACT ITEMS */}
                  <div className="mt-9 space-y-4">

                    {/* PHONE */}
                    <a
                      href="tel:+97144473839"
                      className="group/item flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition-all duration-300 hover:border-white/20 hover:bg-white/[0.12]"
                    >

                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F3D48A] transition-all duration-300 group-hover/item:bg-[#D7A74A] group-hover/item:text-[#690000]">
                        <Phone size={19} />
                      </div>

                      <div className="min-w-0 flex-1">

                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">
                          Call Us
                        </p>

                        <p className="mt-1 text-sm font-bold text-white">
                          +971 4 447 3839
                        </p>

                        <p className="mt-1 text-xs text-white/50">
                          Available for assistance
                        </p>

                      </div>

                      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-white/40 transition-all group-hover/item:-translate-y-1 group-hover/item:translate-x-1 group-hover/item:text-[#F3D48A]" />

                    </a>

                    {/* EMAIL */}
                    <a
                      href="mailto:info@southtravels.com"
                      className="group/item flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition-all duration-300 hover:border-white/20 hover:bg-white/[0.12]"
                    >

                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F3D48A] transition-all duration-300 group-hover/item:bg-[#D7A74A] group-hover/item:text-[#690000]">
                        <Mail size={19} />
                      </div>

                      <div className="min-w-0 flex-1">

                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">
                          Email Us
                        </p>

                        <p className="mt-1 break-all text-sm font-bold text-white">
                          info@southtravels.com
                        </p>

                        <p className="mt-1 text-xs text-white/50">
                          Send us your enquiry
                        </p>

                      </div>

                      <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-white/40 transition-all group-hover/item:-translate-y-1 group-hover/item:translate-x-1 group-hover/item:text-[#F3D48A]" />

                    </a>

                    {/* LOCATION */}
                    <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4">

                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F3D48A]">
                        <MapPin size={19} />
                      </div>

                      <div className="min-w-0">

                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">
                          Location
                        </p>

                        <p className="mt-1 text-sm font-bold text-white">
                          Dubai, United Arab Emirates
                        </p>

                        <p className="mt-2 text-xs leading-6 text-white/55">
                          Office 302, 3rd Flr. HDS Tower Cluster F,
                          Jumeirah Lakes Towers Dubai
                        </p>

                      </div>

                    </div>

                  </div>

                  {/* TRUST FOOTER */}
                  <div className="mt-8 flex items-center gap-3 border-t border-white/15 pt-6">

                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#D7A74A]/15">
                      <ShieldCheck size={20} className="text-[#F3D48A]" />
                    </div>

                    <div>

                      <p className="text-xs font-bold text-white">
                        Your information is secure
                      </p>

                      <p className="mt-1 text-[11px] text-white/50">
                        We respect your privacy
                      </p>

                    </div>

                  </div>

                </div>
              </div>

              {/* CONTACT FORM */}
              <div className="relative">

                <div className="h-full rounded-[28px] border border-[#DCE6EC] bg-white p-2 shadow-[0_20px_60px_rgba(15,35,50,0.06)] sm:p-4 lg:p-6">

                  <ContactForm />

                </div>

              </div>

            </div>
          </div>
        </section>

        {/* LOCATION SECTION */}
        <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24">

          <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#D7A74A]/10 blur-3xl" />

          <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

            {/* MAP HEADING */}
            <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full border border-[#8B0000]/10 bg-[#FCEBEC] px-4 py-2">

                  <MapPin size={14} className="text-[#8B0000]" />

                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8B0000]">
                    Find Us
                  </span>

                </div>

                <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-[#172B35] sm:text-4xl lg:text-5xl">
                  Our Location
                </h2>

                <div className="mt-4 h-1 w-14 rounded-full bg-[#D7A74A]" />

                <p className="mt-4 max-w-xl text-sm leading-7 text-[#71808A] sm:text-base">
                  Find us in Dubai and get in touch with our
                  team for your travel requirements.
                </p>

              </div>

              <div className="inline-flex items-center gap-3 rounded-full border border-[#DCE6EC] bg-[#F5F8FA] px-5 py-3">

                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8B0000] text-white">
                  <MapPin size={16} />
                </div>

                <span className="text-sm font-bold text-[#172B35]">
                  Dubai, United Arab Emirates
                </span>

              </div>

            </div>

            {/* MAP CONTAINER */}
            <div className="relative overflow-hidden rounded-[28px] border border-[#DCE6EC] bg-white p-2 shadow-[0_25px_70px_rgba(15,35,50,0.09)] sm:p-3">

              {/* MAP */}
              <div className="h-[350px] overflow-hidden rounded-[20px] sm:h-[450px] lg:h-[520px]">

                <iframe
                  title="Dubai Marina Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d3613.775341638421!2d55.143142861862316!3d25.0756025365207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sOffice%20302%2C%203rd%20Flr.%20HDS%20Tower%20Cluster%20F%2C%20Jumeirah%20Lakes%20Towers%20Dubai!5e0!3m2!1sen!2sae!4v1789480084690!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />

              </div>

              {/* FLOATING LOCATION CARD */}
              <div className="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:left-7 sm:right-auto sm:max-w-[360px]">

                <div className="rounded-2xl border border-white/80 bg-white/95 p-4 shadow-[0_15px_45px_rgba(15,35,50,0.16)] backdrop-blur-md sm:p-5">

                  <div className="flex items-start gap-4">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#8B0000] text-[#F3D48A]">
                      <MapPin size={20} />
                    </div>

                    <div>

                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B0000]">
                        Visit Us
                      </p>

                      <p className="mt-1 text-sm font-bold text-[#172B35]">
                        Dubai, United Arab Emirates
                      </p>

                      <p className="mt-2 text-xs leading-5 text-[#71808A]">
                        Contact our team for directions
                        and assistance.
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </div>
          </div>
        </section>

      </div>
    </>
  );
}